/**
 * Short plain-English notes shown next to KVP entries (Settings -> KVP). The
 * graphs themselves are in kvpGraphs.tsx.
 *
 * This is deliberately a static module rather than i18n rows: it documents how
 * the backend behaves, is read only by super admins, and has to change in the
 * same commit as the code it describes. Keep it in sync with:
 *   - backend/src/utils/conversation_analysis.py, bug_report_agent.py,
 *     preference_reduction.py, system_prompts.py,
 *     services/memory_service.py                   (system_prompt.*)
 *   - backend/src/services/transcription_summary_service.py,
 *     document_summary_service.py                  (default prompt set)
 *   - backend/src/utils/chat_client_utils.py, tool_executor.py (tool.*)
 *   - backend/src/utils/skills.py                  (skill.*)
 *   - backend/src/utils/user_actions.py, system_state.py (user_action.*, system_state.*)
 */

export type KvpGroupId = "system_prompt" | "tool" | "skill" | "user_action" | "system_state";

export type KvpGroupDoc = {
    /** Human name shown in the overview and next to the raw key in the tree. */
    title: string;
    /** One or two sentences: what this branch of the tree is for. */
    summary: string;
};

export type KvpKeyDoc = {
    /** Short human name, also shown in the tree. */
    name?: string;
    /** One sentence. */
    note: string;
};

export const KVP_GROUP_DOCS: Record<KvpGroupId, KvpGroupDoc> = {
    system_prompt: {
        title: "System prompts",
        summary: "One prompt per background peer agent, plus the base layer prepended for Eyka, the analyst and the ticketing agent. Per-organisation identity and pre-prompt live in the org's prompt set, not here.",
    },
    tool: {
        title: "Tools",
        summary: "Descriptions of the shared init toolset (Eyka and peer agents get the same list, which keeps the prompt cache warm). Behaviour is code; only what the model reads is editable.",
    },
    skill: {
        title: "Skills",
        summary: "Prompt + optional side effects, started by the user via the / palette or by Eyka via skills_tool.",
    },
    user_action: {
        title: "User actions",
        summary: "Templates that turn stored user_action messages into the text Eyka reads at send time. The chat UI does not use them. Unknown (action, object_type) pairs use fallback.",
    },
    system_state: {
        title: "System state",
        summary: "Template for the selection snapshot injected before each user message.",
    },
};

export const KVP_KEY_DOCS: Record<string, KvpKeyDoc> = {
    // system prompts
    "system_prompt.eyka_infrastructure": {
        name: "Base layer",
        note: "Appended after identity + pre-prompt for Eyka, the analyst and the ticketing agent. No code fallback: must exist.",
    },
    "system_prompt.initial_conversation_status_message_generation": {
        name: "Status writer",
        note: "Runs once after the first answered user message; writes the conversation status / title.",
    },
    "system_prompt.conversation_analysis": {
        name: "Analyst",
        note: "Runs when a conversation passes its size thresholds, on Eyka's cached context and before older messages move into history; writes status, preferences and optionally a mail.",
    },
    "system_prompt.memory_headline": {
        name: "Memory writer",
        note: "Runs in the background once 12K characters of history have no headline; writes one immutable headline (at most 400 characters, with history citations) per 12-16K chunk. Empty: a code fallback is used.",
    },
    "system_prompt.preference_reduction": {
        name: "Preference reducer",
        note: "Runs when a user has more than 5 preferences; replaces the whole list.",
    },
    "system_prompt.ticketing_agent": {
        name: "Ticketing agent",
        note: "Started when Eyka calls start_bug_report; sends exactly one support mail.",
    },
    "system_prompt.tool_error_agent": {
        name: "Tool error agent",
        note: "Started on a failed tool call in Eyka or any peer, with the context at the failure; sends one [Eyka tool error] mail per new fingerprint. Repeats within 6 hours are only logged.",
    },
    "system_prompt.skill_author": {
        name: "Skill author",
        note: "Started when Eyka calls skills_tool create or update, on Eyka's cached context; writes the skill, dry-runs it in a sandbox at most twice and saves it as a user skill. Empty: a code fallback is used.",
    },
    "system_prompt.default_prompt_set": {
        name: "Default prompt set",
        note: "Fallback for each summarisation field an organisation's prompt set leaves empty. Empty in both: that summary is skipped.",
    },

    // tools
    "tool.cruq_tool": { note: "Create / read / update / query workspace objects. Permission level 2+ only." },
    "tool.validate_citation": { note: "Registers citations of artifacts / events. Permission level 2+ only." },
    "tool.current_time_tool": { note: "Current time in Europe/Copenhagen." },
    "tool.get_conversation_constants": { note: "User name, preferences and tags for the conversation." },
    "tool.modify_user_preferences_tool": { note: "Adds / removes user preferences; may start the preference reducer." },
    "tool.set_selected_objects_tool": { note: "Lets Eyka change the user's selection in the UI." },
    "tool.skills_tool": { note: "Lets Eyka list (help), read and invoke skills, and create or update user skills through the skill author." },
    "tool.get_dynamic_tool_definitions": { note: "Returns the extra tools for the current context; their schemas arrive as a tool result." },
    "tool.call_dynamic_tool": { note: "Calls one of the extra tools returned by get_dynamic_tool_definitions." },
    "tool.start_bug_report": { note: "Blurb of the dynamic start_bug_report tool. Not in the init list; reached via call_dynamic_tool." },

    // skills
    "skill.create_artifact": { note: "Creates an artifact first, then gives Eyka the prompt with {created_artifact_id} filled in." },

    // user actions
    "user_action.create_artifact": { note: "User created an artifact. 'deleted' is used instead if the artifact no longer exists at send time." },
    "user_action.create_tag": { note: "User created a tag." },
    "user_action.create_event": { note: "User created an event." },
    "user_action.create_document": { note: "User uploaded a document." },
    "user_action.delete_artifact": { note: "User deleted an artifact." },
    "user_action.delete_tag": { note: "User deleted a tag." },
    "user_action.delete_event": { note: "User deleted an event." },
    "user_action.delete_document": { note: "User deleted a document." },
    "user_action.update_document": { note: "User saved a document." },
    "user_action.update_event": { note: "User saved an event." },
    "user_action.update_tag": { note: "User saved a tag." },
    "user_action.update_artifact": { note: "User saved an artifact; says which of Eyka's revisions were applied or discarded." },
    "user_action.revision_artifact": { note: "Eyka's revision was committed while the user had unsaved edits." },
    "user_action.user_changes_artifact": { note: "Unsaved editor changes sent along with a message. Never stored." },
    "user_action.artifact_variable_name_update": { note: "User renamed an artifact." },
    "user_action.revisions_discarded_artifact": { note: "A save elsewhere discarded Eyka's open revisions on an artifact." },
    "user_action.revisions_discarded_tag": { note: "A save elsewhere discarded Eyka's open revisions on a tag." },
    "user_action.fallback": { note: "Used for any (action, object_type) pair without its own template." },

    // system state
    "system_state.selected_objects": {
        name: "Selection snapshot",
        note: "What the user has selected and open. Injected before the user's message; hidden in the chat UI.",
    },
};

export function groupIdForKey(path: string): KvpGroupId | null {
    const root = path.split(".")[0];
    return root in KVP_GROUP_DOCS ? (root as KvpGroupId) : null;
}

/** Human title for a tree row; `null` means show only the raw segment. */
export function groupTitleForPath(path: string): string | null {
    if (path in KVP_GROUP_DOCS) return KVP_GROUP_DOCS[path as KvpGroupId].title;
    return KVP_KEY_DOCS[path]?.name ?? null;
}
